import * as Sentry from '@sentry/nextjs'
import settings from '@/settings/public'
import { getLocalStorageData, setLocalStorageData, removeLocalStorageData } from '@/utils/handleLocalStorage'
import { IEntitlementRecord } from './types'
import {
  isEntitlementRecordExpired,
  isEntitlementRecordUsable,
  normalizeEntitlementId,
  parseEntitlementRecord,
} from './entitlement'

/**
 * Persistence for the activation `entitlementId` (PRO-13). See
 * `docs/ACTIVATION.md` for the full lifecycle.
 *
 * The URL is the source; this store is only the carrier for the one hop the
 * URL cannot make — the password-recovery mail opening in a fresh tab.
 * localStorage on purpose: every logout path (including the `forceLogout`
 * teardown the `/entel` scene itself triggers) calls `sessionStorage.clear()`,
 * so a sessionStorage record would be wiped moments after capture. All access
 * goes through `handleLocalStorage`, which is a no-op server-side and swallows
 * blocked-storage throws (iOS private mode, SMS webviews) — in those browsers
 * the flow degrades to URL-only, which still covers the same-tab journey.
 */

// Same app-scoped key convention as useBanner.
const STORAGE_KEY = `_${settings.app?.code}:activation:entitlement`

/**
 * Session-scoped consumed guard for browsers where localStorage writes are
 * blocked: the tombstone cannot be persisted there, and without it the
 * consumed check stays permanently false while nuqs keeps the param in the
 * address bar — the already-linked id would ride every later auth on the tab.
 * Per-document by nature, which matches what those browsers can support at
 * all; a reload falls back to the stored tombstone where storage works.
 * Never touched server-side (every entry point below guards on `window`), so
 * the module-scope Set cannot leak ids across server requests.
 */
const consumedIdsThisSession = new Set<string>()

// Once per page load: every store call re-reads the record, and a flood of
// identical reports adds nothing over the first.
let hasReportedCorruptRecord = false

const readRecord = (): IEntitlementRecord | null => {
  const stored = getLocalStorageData(STORAGE_KEY)
  const record = parseEntitlementRecord(stored)

  // Present but unparseable is degradation, not absence: a systematic
  // corruption source (a serialization bug, another feature clobbering the
  // key) would otherwise null every activation invisibly — a swallowed error
  // must reach Sentry.
  if (stored && !record && !hasReportedCorruptRecord) {
    hasReportedCorruptRecord = true
    Sentry.captureException(
      new Error('Unparseable activation entitlement record in localStorage'),
      { level: 'warning' }
    )
  }

  return record
}

const writeRecord = (record: IEntitlementRecord): void => {
  setLocalStorageData(STORAGE_KEY, JSON.stringify(record))
}

/**
 * Stores an id arriving in the URL. Idempotent for an id we already hold and
 * that has not expired — `GeoLocationGuard` remounts the whole scene on
 * geolocation refetches, and a remount must neither reset `capturedAt` nor
 * resurrect a consumed record. Once the record has expired (one clock for
 * consumed and unconsumed alike), a same-id arrival is a fresh activation —
 * the partner re-sent the link — and writes anew. A *different* id always
 * overwrites: a new activation wins.
 */
export const captureEntitlementId = (id: string): void => {
  const normalized = normalizeEntitlementId(id)

  if (!normalized) return

  const existing = readRecord()

  if (existing?.id === normalized && !isEntitlementRecordExpired(existing, Date.now())) return

  writeRecord({ id: normalized, capturedAt: Date.now() })
}

/**
 * The id to attach to an auth payload right now, or null. Expired records —
 * consumed tombstones included — are purged on read so nothing outlives its
 * TTL in storage.
 */
export const getActiveEntitlementId = (): string | null => {
  const record = readRecord()

  if (!record) return null

  const now = Date.now()

  if (!isEntitlementRecordUsable(record, now)) {
    if (isEntitlementRecordExpired(record, now)) {
      removeLocalStorageData(STORAGE_KEY)
    }
    return null
  }

  return record.id
}

/**
 * True while this exact id is known consumed: either tombstoned in storage
 * (and not expired) or consumed earlier in this page session — the in-memory
 * set is what still guards the URL-first read when localStorage is blocked
 * and no tombstone could be written. Guards `useActivationEntitlement`: nuqs
 * keeps the param in the address bar for the whole same-tab session, so after
 * a successful auth the URL still carries an id that was already linked.
 */
export const isEntitlementIdConsumed = (id: string): boolean => {
  if (typeof window === 'undefined') return false

  const normalized = normalizeEntitlementId(id)

  if (!normalized) return false
  if (consumedIdsThisSession.has(normalized)) return true

  const record = readRecord()

  if (!record || record.id !== normalized) return false
  if (record.consumedAt === undefined) return false

  return !isEntitlementRecordExpired(record, Date.now())
}

/**
 * Tombstones after a successful auth instead of deleting: the URL may still
 * carry the id, and a reload must not re-capture an id that was already
 * linked. Pass the id the auth actually saw when one is known — consumption
 * is then scoped to it, so a record another tab wrote meanwhile (storage is
 * last-write-wins) is never tombstoned by an auth that did not carry it.
 * Without an id, the stored record is consumed: that is the recovery-bounce
 * login, where the id comes from the store itself.
 */
export const markEntitlementConsumed = (id?: string): void => {
  if (typeof window === 'undefined') return

  const record = readRecord()
  const target = id !== undefined ? normalizeEntitlementId(id) : record?.id ?? null

  if (!target) return

  consumedIdsThisSession.add(target)

  if (!record || record.id !== target || record.consumedAt !== undefined) return

  writeRecord({ ...record, consumedAt: Date.now() })
}

/**
 * Full reset: stored record and the session consumed-set. No production
 * caller by design — the lifecycle deliberately never clears on logout (see
 * docs/ACTIVATION.md), so never wire this into a logout path. Exists for test
 * isolation and support tooling only.
 */
export const clearEntitlementRecord = (): void => {
  consumedIdsThisSession.clear()
  removeLocalStorageData(STORAGE_KEY)
}
